import { type RefObject, useEffect, useRef } from 'react'

// A swipe pager's tab header is drawn twice. Each pane renders a copy at the top of its
// own scroller ([data-pane-tabs]), so a vertical scroll moves it with the content on the
// compositor and it can never drift. Only while a swipe or tab change animates does the
// overlay ([data-tabs-bar]) take over, following JS-interpolated scroll offsets. A
// scroll-timeline animation on a single overlay lagged the content by a frame or more
// whenever the main thread was busy. See [data-tabs-float] in index.css.
export function usePagerHeader(
  pagerRef: RefObject<HTMLElement | null>,
  scrollerAt: (i: number) => HTMLElement | null | undefined,
  count: number,
  enabled = true,
) {
  const headerRef = useRef<HTMLDivElement>(null)
  const landTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const scrollerRef = useRef(scrollerAt)
  scrollerRef.current = scrollerAt

  const move = (pos: number, animate = false) => {
    const h = headerRef.current
    if (!h) return
    const a = Math.floor(pos)
    const b = Math.min(a + 1, count - 1)
    const ya = scrollerRef.current(a)?.scrollTop ?? 0
    const yb = scrollerRef.current(b)?.scrollTop ?? 0
    const y = ya + (yb - ya) * (pos - a)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Reading offsetHeight flushes style, so an animated move starts from the last one.
    const max = h.offsetHeight + h.offsetTop
    h.style.transition = animate && !reduce ? 'transform 250ms ease-out' : ''
    h.style.transform = `translateY(${-Math.min(Math.max(y, 0), max)}px)`
  }
  const unfloat = () => {
    clearTimeout(landTimer.current)
    pagerRef.current?.removeAttribute('data-tabs-float')
  }
  // Swap to the overlay, placed exactly over the copy in pane `pos`.
  const float = (pos: number) => {
    clearTimeout(landTimer.current)
    const pager = pagerRef.current
    if (!pager || pager.hasAttribute('data-tabs-float')) return
    move(pos)
    pager.setAttribute('data-tabs-float', '')
  }
  const land = () => {
    clearTimeout(landTimer.current)
    landTimer.current = setTimeout(unfloat, 350)
  }

  useEffect(() => {
    const pager = pagerRef.current
    if (!enabled || !pager) return
    // Any new gesture or scroll mid-settle hands the header straight back to the panes,
    // before the compositor starts moving them. A horizontal drag floats it again on lock.
    const hand = () => {
      if (pager.hasAttribute('data-tabs-float') && !pager.hasAttribute('data-dragging')) unfloat()
    }
    const opts = { capture: true, passive: true }
    for (const type of ['touchstart', 'wheel', 'scroll']) pager.addEventListener(type, hand, opts)
    return () => {
      for (const type of ['touchstart', 'wheel', 'scroll'])
        pager.removeEventListener(type, hand, { capture: true })
      clearTimeout(landTimer.current)
    }
  }, [pagerRef, enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  return { headerRef, move, float, land }
}
